import { Track } from "@/src/lib/tracking";
import { wrapperResult } from "@api/osp";
import { proxy } from "valtio";

import { getItem, setItem } from "utils";
import { authCacheManager } from "@features/auth/compat/auth-cache-compat";
import { subscribeKey } from "valtio/utils";
import type { EdgenUserInfo, UserState } from "../types";
import { LOCAL_STORAGE_KEYS } from "@config/constant";
import {
  clearShellUserProfileOnNativeIfInApp,
  syncShellUserProfileToNativeIfInApp,
} from "@/src/lib/bridge/shellUserProfileNativeSync";
import { sendLoginEvent } from "@utils/sendGtmOrApp";

/**
 * 获取缓存的用户信息和缓存对象
 * @returns 缓存的用户信息缓存对象，如果不存在则返回 null
 */
function getCachedOwnerInfo() {
  const cachedUserId = getItem("_user_id") as string;
  if (!cachedUserId) {
    return null;
  }
  return authCacheManager.getOwnerInfoCache(cachedUserId);
}

/** 延后网络拉取只执行一次；登出时由 clearUser 重置 */
let hasFetchedUserInfo = false;

export function resetFetchUserInfoDeferredState() {
  hasFetchedUserInfo = false;
}

/**
 * apiClient 就绪后补拉用户信息（兼容 initFromCache 早于 Provider 的时序，见 spec X6）
 */
export function runDeferredFetchUserInfo() {
  if (!UserStore.apiClient || hasFetchedUserInfo) {
    return;
  }

  // 避免 user ↔ login 顶层循环依赖

  const { LoginStore } = require("./login") as typeof import("./login");
  const eoaAddress = LoginStore.eoaAddress || UserStore.eoaAddress;
  const isLoggedIn = LoginStore.isLoggedIn;

  if (!eoaAddress && !isLoggedIn) {
    return;
  }

  hasFetchedUserInfo = true;

  void UserStore.fetchUserInfo(eoaAddress || "", eoaAddress ? undefined : true);
}

export const UserStore = proxy<UserState>({
  ownerInfo: null,
  accessToken: null,
  profileState: "notFetched",
  turnstileToken: null,
  locale: "de",
  enableNotification: false,
  clearUser: () => {
    UserStore.ownerInfo = null;
    UserStore.eoaAddress = "";
    UserStore.accessToken = "";
    UserStore.turnstileToken = null;
    UserStore.profileState = "notFetched";
    resetFetchUserInfoDeferredState();
    clearShellUserProfileOnNativeIfInApp();
    // 清除缓存
    authCacheManager.clearOwnerInfoCache();
  },
  /**
   * 从缓存恢复用户信息（激进策略）
   * 只从缓存读取，不发起网络请求
   * @returns {boolean} 是否成功从缓存恢复
   */
  fetchUserInfoFromCache: (): boolean => {
    try {
      const cachedOwnerInfoCache = getCachedOwnerInfo();

      if (!cachedOwnerInfoCache?.ownerInfo) {
        return false;
      }

      // 从缓存恢复状态（快速恢复）
      UserStore.ownerInfo = {
        ...cachedOwnerInfoCache.ownerInfo,
        name:
          cachedOwnerInfoCache.ownerInfo?.issue_name ??
          cachedOwnerInfoCache.ownerInfo?.issue_email,
      };
      UserStore.profileState = "hasProfile";

      // 处理通知配置
      try {
        if (UserStore?.ownerInfo?.id) {
          const notificationConfigs: Record<string, boolean> =
            getItem(LOCAL_STORAGE_KEYS.NOTIFICATION_CONFIG) || {};
          const userNotificationFlag =
            notificationConfigs[UserStore.ownerInfo.id];
          if (userNotificationFlag === undefined) {
            UserStore.enableNotification = true;
            notificationConfigs[UserStore.ownerInfo.id] = true;
            setItem(
              LOCAL_STORAGE_KEYS.NOTIFICATION_CONFIG,
              notificationConfigs,
            );
          } else {
            UserStore.enableNotification = userNotificationFlag;
          }
        }
      } catch (error) {
        Track.error(error, {
          tag: "login",
          msg: "fetchUserInfoFromCache: set notification config error",
        });
      }

      return true;
    } catch (error) {
      Track.error(error, { tag: "login", msg: "fetchUserInfoFromCache error" });
      return false;
    }
  },
  fetchUserInfo: async (eoaAddress?: string, isLoggedIn?: boolean) => {
    if (!UserStore.apiClient) {
      return;
    }
    if (!eoaAddress && !isLoggedIn) {
      throw new Error("eoaAddress or isLoggedIn is required");
    }

    try {
      UserStore.profileState = "fetching";
      const ownerInfo = await wrapperResult(
        UserStore.apiClient?.owners.getOwnerInfo(),
      );
      const userInfo: EdgenUserInfo = ownerInfo;

      if (userInfo?.id) {
        // const profileInfo = userInfo?.profile?.find?.(
        //   (item) => item.chain_id === config.chainConfig.id.toString(),
        // );
        // const ownerInfo = {
        //   ...userInfo,
        //   // ospProfile: profileInfo,
        //   name: userInfo?.issue_name ?? userInfo?.issue_email,
        // };
        UserStore.ownerInfo = {
          ...userInfo,
          // ospProfile: profileInfo,
          name: userInfo?.issue_name ?? userInfo?.issue_email,
        };
        setItem("_user_id", UserStore.ownerInfo.id);

        // 保存到缓存
        authCacheManager.saveOwnerInfoCache(userInfo, userInfo.id);

        try {
          if (UserStore.ownerInfo.id) {
            const notificationConfigs: Record<string, boolean> =
              getItem(LOCAL_STORAGE_KEYS.NOTIFICATION_CONFIG) || {};
            const userNotificationFlag =
              notificationConfigs[UserStore.ownerInfo.id];
            if (userNotificationFlag === undefined) {
              UserStore.enableNotification = true;
              notificationConfigs[UserStore.ownerInfo.id] = true;
              setItem(
                LOCAL_STORAGE_KEYS.NOTIFICATION_CONFIG,
                notificationConfigs,
              );
            } else {
              UserStore.enableNotification = userNotificationFlag;
            }
          }
        } catch (error) {
          Track.error(error, {
            tag: "login",
            msg: "set notification config error",
          });
        }
        UserStore.profileState = "hasProfile";
      } else {
        UserStore.profileState = "error";
      }
    } catch (error) {
      Track.error(error, { tag: "login", msg: "fetchUserInfo error" });
      UserStore.profileState = "error";
    }
  },
});

subscribeKey(UserStore, "apiClient", (value) => {
  if (value) {
    runDeferredFetchUserInfo();
  }
});
subscribeKey(UserStore, "ownerInfo", (ownerInfo) => {
  if (ownerInfo?.id) {
    sendLoginEvent(ownerInfo.id, ownerInfo?.issue_platform ?? "");
  }
  syncShellUserProfileToNativeIfInApp();
});
